import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import ReactQuill, { Quill } from "react-quill";
import { Select, Tooltip, Modal, Input, Button } from "antd";
import "react-quill/dist/quill.snow.css";
import { useSelector } from "react-redux";
import { isNSI } from "../../../utils";
import ChoiceOptions from '../../../v2/builder/Builder/Sidebar/Settings/Components/TextLanguage/ChoiceOptions';
import Phone from '../../../v2/builder/Builder/Sidebar/Settings/Components/TextLanguage/Phone';

const { Option } = Select;

const FontAttributor = Quill.import("attributors/class/font");
FontAttributor.whitelist = [
  "arial", "comicsans", "couriernew", "georgia",
  "lucidasans", "tahoma", "timesnewroman", "trebuchet", "verdana", 'NSI'
];

const Size = Quill.import("formats/size");
Size.whitelist = ["small", "normal", "large", "huge", "custom1", "custom2"];
Quill.register(Size, true);

// Register list group attributor to prevent list merging
const Parchment = Quill.import("parchment");

class ListGroupAttributor extends Parchment.Attributor.Attribute { }
const ListGroup = new ListGroupAttributor('listgroup', 'data-list-id', {
  scope: Parchment.Scope.BLOCK
});
Quill.register(ListGroup, true);

const DEFAULT_SIZE_MAP = {
  small: "0.857rem",
  normal: "1.143rem",
  large: "1.429rem",
  huge: "2rem",
  custom1: "1.143rem",
  custom2: "1.143rem",
};

const DEFAULT_MARGIN_MAP = {
  top: "1em",
  bottom: "1em",
  left: "1em",
  right: "1em",
  paragraphSpacing: "0.5em"
};

const DEFAULT_LIST_STYLES = {
  listLeftMargin: "2em",
  itemSpacing: "0.5em",
  markerSpace: "0.5em",
  listType: "decimal"
};

const FONT_OPTIONS = [
  { label: "Arial", value: "arial", style: { fontFamily: "Arial, sans-serif" } },
  { label: "Comic Sans", value: "comicsans", style: { fontFamily: "'Comic Sans MS', cursive, sans-serif" } },
  { label: "Courier New", value: "couriernew", style: { fontFamily: "'Courier New', monospace" } },
  { label: "Georgia", value: "georgia", style: { fontFamily: "Georgia, serif" } },
  { label: "Lucida Sans", value: "lucidasans", style: { fontFamily: "'Lucida Sans', sans-serif" } },
  { label: "Tahoma", value: "tahoma", style: { fontFamily: "Tahoma, Geneva, sans-serif" } },
  { label: "Times New Roman", value: "timesnewroman", style: { fontFamily: "'Times New Roman', serif" } },
  { label: "Trebuchet", value: "trebuchet", style: { fontFamily: "'Trebuchet MS', sans-serif" } },
  { label: "Verdana", value: "verdana", style: { fontFamily: "Verdana, Geneva, sans-serif" } },
];

const customLinkHandler = async (quill, modal, formatName, formatArgsFn) => {
  try {
    const format = quill.getFormat();

    let { index: selectionIndex, length: selectionLength } = quill.getSelection(true);
    const selectedText = quill.getText(selectionIndex, selectionLength);

    if (format['link'] || format[formatName]) {
      selectionIndex += 1;
      const [leaf, leafOffset] = quill.getLeaf(selectionIndex);
      quill.setSelection(selectionIndex - leafOffset, leaf.length());
    } else if (!selectedText.length || !selectedText.trim().length) {
      throw new Error('no text selected');
    }

    const result = await modal.show(format[formatName] && format[formatName].choice);
    if (result) {
      quill.format('link', false);
      quill.format(formatName, false);
      quill.format(formatName, formatArgsFn(result));
    }
  } catch (_) { }
};

async function phoneHandler(modal) {

  const range = this.quill.getSelection(true);

  let initialValue = '';

  if (range) {
    const [leaf] = this.quill.getLeaf(range.index);

    const domNode = leaf?.domNode?.parentNode; // <a> tag
    if (domNode?.tagName === 'A') {
      const href = domNode.getAttribute('href');
      if (href?.startsWith('tel:')) {
        initialValue = href.replace(/^tel:\+?/, '');
      }
    }
  }

  const result = await modal.show(initialValue);

  if (!result) return;

  this.quill.formatText(range.index, range.length, "link", 'tel:+' + result);
}

const TextEditor = ({ value, onChange, disabled = false, features, sites, getWrappedHtmlRef }) => {
  const BaseLink = Quill.import('formats/link');

  class AccessibleLink extends BaseLink {
    static create(value) {
      const node = super.create(value);
      node.setAttribute('href', value);
      node.setAttribute('aria-label', value);

      return node;
    }

    static formats(node) {
      return node.getAttribute('href'); // MUST be a string
    }
  }

  Quill.register(AccessibleLink, true);

  const siteId = parseInt(localStorage.getItem('spt_site_id'));
  const currentSite = sites.find(({ id }) => id === siteId);
  const isApp = currentSite ? currentSite.type === 'app' : null;

  const [settings, setSettings] = useState({
    sizeMap: DEFAULT_SIZE_MAP,
    marginMap: DEFAULT_MARGIN_MAP,
    listStyles: {},
    choiceOptions: []
  });


  const [currentListId, setCurrentListId] = useState(null);

  const [showEditor, setShowEditor] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showMarginModal, setShowMarginModal] = useState(false);

  const quillRef = useRef(null);
  const selectionRef = useRef(null);
  const coRef = useRef(null);
  const phoneRef = useRef(null);

  const modulesMemo = useMemo(() => ({
    toolbar: {
      container: "#custom-toolbar",
      handlers: {
        linkChoiceOptions: function () {
          const quill = quillRef.current?.getEditor();
          if (!quill) return;
          customLinkHandler(
            quill,
            coRef.current,
            "linkChoiceOptions",
            result => ({ choice: result.data.button_text })
          );
        },
        ['phone']: function () {
          phoneHandler.call(this, phoneRef.current)
        },
        'indent': function (value) {
          const quill = this.quill;
          const range = quill.getSelection(true);
          if (!range) {
            return;
          }

          // Ensure lines are lists before changing indent
          const fmt = quill.getFormat(range);
          const isList = fmt.list === 'ordered' || fmt.list === 'bullet';

          if (!isList) {
            // Make current lines ordered (or choose 'bullet' per your UI)
            quill.formatLine(range.index, range.length, { list: 'ordered' }, 'user');

          }

          // Figure current indent; Quill returns a number when present
          const current = (quill.getFormat(range).indent || 0);

          if (value === '+1') {
            const newIndent = current + 1;
            quill.formatLine(range.index, range.length, { indent: newIndent }, 'user');
          } else if (value === '-1') {
            const next = Math.max(0, current - 1);
            // If indent drops to 0, you may want to keep it a list at level 0
            quill.formatLine(range.index, range.length, { indent: next }, 'user');
          }

          // Log the result and apply proper list styling after a short delay
          setTimeout(() => {
            const newFmt = quill.getFormat(range);

            // Find the list that contains the indented items
            const [leaf] = quill.getLeaf(range.index);
            let domNode = leaf?.domNode;

            // Find the closest list element
            while (domNode && !['OL', 'UL'].includes(domNode.tagName)) {
              domNode = domNode.parentElement;
            }

            if (domNode && domNode.tagName === 'OL') {

              // Get or create a list ID for this list
              let listId = domNode.getAttribute('data-custom-list-id');
              if (!listId) {
                listId = `list-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
                domNode.setAttribute('data-custom-list-id', listId);
              }

              // Detect actual levels in this list and create structure only for those
              const listItems = domNode.querySelectorAll('li');
              const existingLevels = new Set();

              listItems.forEach(item => {
                const indentLevel = parseInt(item.className.match(/ql-indent-(\d+)/)?.[1] || '0');
                existingLevels.add(indentLevel);
              });


              // Use setSettings callback to get the most recent state
              setSettings(currentSettings => {
                // Get the most up-to-date list styles
                const existingListStyles = currentSettings.listStyles[listId] || {};

                const levels = {};

                existingLevels.forEach(level => {
                  // Use existing styles for this level if they exist, otherwise use defaults
                  if (existingListStyles.levels && existingListStyles.levels[level]) {
                    levels[level] = { ...existingListStyles.levels[level] };
                  } else {
                    levels[level] = {
                      listType: DEFAULT_LIST_STYLES.listType,
                      listLeftMargin: DEFAULT_LIST_STYLES.listLeftMargin,
                      itemSpacing: DEFAULT_LIST_STYLES.itemSpacing,
                      markerSpace: DEFAULT_LIST_STYLES.markerSpace
                    };
                  }
                });

                const defaultLevelsStructure = {
                  levels,
                  listPosition: existingListStyles.listPosition || 0
                };

                // Apply the styling using our existing function
                setTimeout(() => {
                  applyListStyleToSelection(quill, range, defaultLevelsStructure);
                }, 10);

                // Return the current settings unchanged since we're just reading
                return currentSettings;
              });

            }

            // Apply styling completed

          }, 100);
        }
      }
    },
    keyboard: {
      bindings: {
        fixTypingBug: { key: null, handler: () => { } }
      }
    }
  }), [coRef, quillRef]);

  const { accountId } = useSelector((state) => state.accountState.getIn(['userDetails', 'value']));



  // Helper function to ensure list group ID is set
  const ensureListGroupId = (quill, range) => {
    const current = quill.getFormat(range).listgroup;
    if (current) {
      return current;
    }
    const id = `list-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    quill.formatLine(range.index, range.length, { listgroup: id }, 'user');
    return id;
  };

  // Helper function to detect the current indent level from selection
  const getCurrentIndentLevel = () => {
    if (!quillRef.current) return 0;

    const quill = quillRef.current.getEditor();
    const selection = quill.getSelection() || selectionRef.current;

    if (!selection || selection.length === 0) return 0;

    // Get the format at the current selection
    const format = quill.getFormat(selection);
    return format.indent || 0;
  };

  // Helper function to get current list styles for a specific level
  const getCurrentListStyles = (level = null) => {
    // If no level specified, use the currently selected level
    const targetLevel = level !== null ? level : getCurrentIndentLevel();

    if (currentListId && settings.listStyles[currentListId] && settings.listStyles[currentListId].levels) {
      return settings.listStyles[currentListId].levels[targetLevel] || { ...DEFAULT_LIST_STYLES };
    }

    // Return defaults for the specified level
    return { ...DEFAULT_LIST_STYLES };
  };



  // Parse settings from value if it contains data-settings attribute
  useEffect(() => {
    if (value && typeof value === 'string' && value.includes('data-settings')) {
      try {
        const wrapper = document.createElement('div');
        wrapper.innerHTML = value;
        const root = wrapper.firstChild;

        if (root && root.getAttribute) {
          const settingsAttr = root.getAttribute('data-settings');


          if (settingsAttr) {
            // Unescape the HTML entities
            const unescapedJson = settingsAttr.replace(/&quot;/g, '"');


            try {
              const parsedSettings = JSON.parse(unescapedJson);


              // Clean up margin values to remove excessive decimals
              if (parsedSettings.marginMap) {
                const cleanedMarginMap = { ...DEFAULT_MARGIN_MAP };
                Object.entries(parsedSettings.marginMap).forEach(([key, value]) => {
                  if (typeof value === 'string' && value.includes('em')) {
                    const numericValue = parseFloat(value);
                    if (!isNaN(numericValue)) {
                      // Round to 1em, 0.5em, 1.5em, etc. (max 1 decimal place)
                      const roundedValue = Math.round(numericValue * 10) / 10;
                      cleanedMarginMap[key] = `${roundedValue}em`;
                    } else {
                      cleanedMarginMap[key] = value;
                    }
                  } else {
                    cleanedMarginMap[key] = value;
                  }
                });
                setSettings(prev => ({ ...prev, marginMap: cleanedMarginMap }));
              } else {
                setSettings(prev => ({ ...prev, marginMap: DEFAULT_MARGIN_MAP }));
              }

              // Update other settings
              if (parsedSettings.sizeMap) {
                setSettings(prev => ({ ...prev, sizeMap: parsedSettings.sizeMap }));
              }
              if (parsedSettings.choiceOptions) {
                setSettings(prev => ({ ...prev, choiceOptions: parsedSettings.choiceOptions }));
              }

              // Parse individual list styles
              if (parsedSettings.listStyles) {
                setSettings(prev => ({ ...prev, listStyles: parsedSettings.listStyles }));
              }
            } catch (parseError) {
              // Could not parse settings from value
            }
          }
        }
      } catch (err) {
        // Could not parse settings from value
      }
    }
  }, [value]);

  // Store the current list's properties when selection changes (but don't override recent changes)
  useEffect(() => {
    if (quillRef.current) {
      const quill = quillRef.current.getEditor();
      if (quill) {
        const updateSettingsFromSelection = () => {
          const selection = quill.getSelection();
          if (selection && selection.length > 0) {
            const leaf = quill.getLeaf(selection.index);
            if (leaf && leaf[0]) {
              let currentNode = leaf[0].domNode;
              let listElement = null;

              // Find the list element
              while (currentNode && currentNode !== quill.root) {
                if (currentNode.tagName === 'OL' || currentNode.tagName === 'UL') {
                  listElement = currentNode;
                  break;
                }
                currentNode = currentNode.parentNode;
              }

              if (listElement) {
                // Get the current list's properties
                const currentListType = listElement.getAttribute('data-list-type');
                const currentLeftMargin = listElement.getAttribute('data-left-margin');
                const currentItemSpacing = listElement.getAttribute('data-item-spacing');
                const currentMarkerSpace = listElement.getAttribute('data-marker-space');
                const existingListId = listElement.getAttribute('data-list-id');

                // Only update if this list has been styled AND we don't have properties for it yet
                if (currentListType && existingListId) {
                  const existingProperties = quillRef.current?.currentListProperties;

                  // Check if we already have properties for this specific list
                  if (!existingProperties || !existingProperties.listId || existingProperties.listId !== existingListId) {
                    const currentListProperties = {
                      listType: currentListType,
                      listLeftMargin: currentLeftMargin || '2em',
                      itemSpacing: currentItemSpacing || '0.5em',
                      markerSpace: currentMarkerSpace || '0.5em',
                      listId: existingListId
                    };

                    quillRef.current.currentListProperties = currentListProperties;

                    // Update current list ID to track which list is selected
                    setCurrentListId(existingListId);
                  }
                }
              }
            }
          }
        };

        // Update on selection change
        quill.on('selection-change', () => {
          // Add safety checks to prevent errors during unmounting
          if (quillRef.current && quill && quill.root && document.contains(quill.root)) {
            try {
              // Simple function to update UI based on current list selection
              const selection = quill.getSelection();
              if (selection && selection.length > 0) {
                const [leaf] = quill.getLeaf(selection.index);
                let domNode = leaf?.domNode;

                // Find the closest list element
                while (domNode && !['OL', 'UL'].includes(domNode.tagName)) {
                  domNode = domNode.parentElement;
                }

                // If we're in a list, update the current list ID
                if (domNode && ['OL', 'UL'].includes(domNode.tagName)) {
                  const listId = domNode.getAttribute('data-list-id') || domNode.getAttribute('data-custom-list-id');

                  if (listId) {
                    setCurrentListId(listId);
                  } else {
                    setCurrentListId(null);
                  }
                }
              }
            } catch (error) {

            }
          }
        });

        return () => {
          quill.off('selection-change');
        };
      }
    }
  }, [quillRef.current]);

  // Restore list styles when component loads
  useEffect(() => {


    if (quillRef.current) {
      const quill = quillRef.current.getEditor();
      if (quill && quill.root) {
        // Check if styles have already been restored
        if (quill.root.getAttribute('data-styles-restored') === 'true') {
          return;
        }

        // Wait for Quill to be fully initialized
        setTimeout(() => {
          if (quill && quill.root) {
            const lists = quill.root.querySelectorAll('ol, ul');

            if (lists.length > 0) {
              // Restore styles for each list individually
              let restoredCount = 0;

              // Get all saved list IDs
              const savedListIds = Object.keys(settings.listStyles);

              if (savedListIds.length > 0 && lists.length > 0) {
                // Create a map of position -> styles for accurate restoration
                const positionToStylesMap = {};

                // Build the position map from saved styles
                savedListIds.forEach(savedListId => {
                  const savedStyles = settings.listStyles[savedListId];
                  if (savedStyles && savedStyles.listPosition !== undefined) {
                    positionToStylesMap[savedStyles.listPosition] = savedStyles;
                  }
                });

                // If we have no position-based styles, fall back to the old method for backward compatibility
                if (Object.keys(positionToStylesMap).length === 0) {
                  // Map saved styles to lists by order (first saved style → first list, etc.)
                  for (let i = 0; i < Math.min(lists.length, savedListIds.length); i++) {
                    const list = lists[i];
                    const savedListId = savedListIds[i];
                    const savedStyles = settings.listStyles[savedListId];

                    if (savedStyles && savedStyles.listType !== 'decimal') {
                      // Reuse the saved ID to maintain consistency
                      list.setAttribute('data-list-id', savedListId);

                      // Apply the saved styles
                      list.setAttribute('data-list-type', savedStyles.listType);
                      list.setAttribute('data-left-margin', savedStyles.listLeftMargin);
                      list.setAttribute('data-item-spacing', savedStyles.itemSpacing);
                      list.setAttribute('data-marker-space', savedStyles.markerSpace);
                      list.setAttribute('data-list-styled', 'true');
                      list.classList.add(savedListId);

                      // Apply the specific properties directly to the list element
                      list.style.paddingLeft = savedStyles.listLeftMargin;
                      list.style.marginBottom = savedStyles.itemSpacing;

                      // Inject CSS for the restored list
                      const listType = savedStyles.listType;
                      let counterStyle = 'decimal';
                      let suffix = '.';
                      if (listType === 'lower-alpha-paren') {
                        counterStyle = 'lower-alpha';
                        suffix = ')';
                      } else if (listType === 'upper-alpha-paren') {
                        counterStyle = 'upper-alpha';
                        suffix = ')';
                      }

                      const styleId = `list-style-${savedListId}`;
                      const cssContent = `
                        html body .ql-editor .${savedListId},
                        html body .ql-editor [data-list-id="${savedListId}"] {
                          list-style: none !important;
                          counter-reset: custom-counter !important;
                          padding-left: ${savedStyles.listLeftMargin} !important;
                        }
                        
                        html body .ql-editor .${savedListId} > li,
                        html body .ql-editor [data-list-id="${savedListId}"] > li {
                          position: relative !important;
                          counter-increment: custom-counter !important;
                          margin-bottom: ${savedStyles.itemSpacing} !important;
                        }
                        
                        html body .ql-editor .${savedListId} > li::before,
                        html body .ql-editor [data-list-id="${savedListId}"] > li::before {
                          content: counter(custom-counter, ${counterStyle}) "${suffix}" !important;
                          position: absolute !important;
                          left: 0 !important;
                          width: calc(2em + ${savedStyles.markerSpace}) !important;
                          display: inline-block !important;
                          text-align: right !important;
                          color: inherit !important;
                          font-weight: normal !important;
                          font-size: inherit !important;
                          z-index: 9999 !important;
                        }
                      `;

                      const styleEl = document.createElement("style");
                      styleEl.id = styleId;
                      styleEl.innerHTML = cssContent;
                      document.head.appendChild(styleEl);

                      restoredCount++;
                    }
                  }
                } else {
                  // Apply styles to lists by their stored position
                  for (let i = 0; i < lists.length; i++) {
                    const list = lists[i];
                    const savedStyles = positionToStylesMap[i];

                    if (savedStyles && savedStyles.listType !== 'decimal') {
                      // For position-based restoration, we need to find the original list ID
                      // Look for a saved list that matches this position
                      let originalListId = null;
                      Object.entries(settings.listStyles).forEach(([id, styles]) => {
                        if (styles.listPosition === i) {
                          originalListId = id;
                        }
                      });

                      // If no original ID found, generate a stable one
                      if (!originalListId) {
                        originalListId = `list-${Date.now()}-${i}-${Math.random().toString(36).substr(2, 9)}`;
                      }

                      list.setAttribute('data-list-id', originalListId);

                      // Apply the saved styles
                      list.setAttribute('data-list-type', savedStyles.listType);
                      list.setAttribute('data-left-margin', savedStyles.listLeftMargin);
                      list.setAttribute('data-item-spacing', savedStyles.itemSpacing);
                      list.setAttribute('data-marker-space', savedStyles.markerSpace);
                      list.setAttribute('data-list-styled', 'true');
                      list.classList.add(originalListId);

                      // Apply the specific properties directly to the list element
                      list.style.paddingLeft = savedStyles.listLeftMargin;
                      list.style.marginBottom = savedStyles.itemSpacing;

                      // Inject CSS for the restored list
                      const listType = savedStyles.listType;
                      let counterStyle = 'decimal';
                      let suffix = '.';
                      if (listType === 'lower-alpha-paren') {
                        counterStyle = 'lower-alpha';
                        suffix = ')';
                      } else if (listType === 'upper-alpha-paren') {
                        counterStyle = 'upper-alpha';
                        suffix = ')';
                      }

                      const styleId = `list-style-${originalListId}`;
                      const cssContent = `
                        html body .ql-editor .${originalListId},
                        html body .ql-editor [data-list-id="${originalListId}"] {
                          list-style: none !important;
                          counter-reset: custom-counter !important;
                          padding-left: ${savedStyles.listLeftMargin} !important;
                        }
                        
                        html body .ql-editor .${originalListId} > li,
                        html body .ql-editor [data-list-id="${originalListId}"] > li {
                          position: relative !important;
                          counter-increment: custom-counter !important;
                          margin-bottom: ${savedStyles.itemSpacing} !important;
                        }
                        
                        html body .ql-editor .${originalListId} > li::before,
                        html body .ql-editor [data-list-id="${originalListId}"] > li::before {
                          content: counter(custom-counter, ${counterStyle}) "${suffix}" !important;
                          position: absolute !important;
                          left: 0 !important;
                          width: calc(2em + ${savedStyles.markerSpace}) !important;
                          display: inline-block !important;
                          text-align: right !important;
                          color: inherit !important;
                          font-weight: normal !important;
                          font-size: inherit !important;
                          z-index: 9999 !important;
                        }
                      `;

                      const styleEl = document.createElement("style");
                      styleEl.id = styleId;
                      styleEl.innerHTML = cssContent;
                      document.head.appendChild(styleEl);

                      restoredCount++;
                    }
                  }
                }

                // Apply default styles to any remaining lists
                for (let i = savedListIds.length; i < lists.length; i++) {
                  const list = lists[i];
                  const listId = `list-default-${i}-${Math.random().toString(36).substr(2, 9)}`;

                  list.setAttribute('data-list-id', listId);
                  list.setAttribute('data-left-margin', '2em');
                  list.setAttribute('data-item-spacing', '0.5em');
                  list.setAttribute('data-marker-space', '0.5em');
                  list.setAttribute('data-list-type', 'decimal');
                  list.classList.add(listId);

                  // Apply default styles
                  list.style.paddingLeft = '2em';
                  list.style.marginBottom = '0.5em';
                }
              } else {
                // No saved styles, apply default styles to all lists
                for (let i = 0; i < lists.length; i++) {
                  const list = lists[i];
                  const listId = `list-default-${i}-${Math.random().toString(36).substr(2, 9)}`;

                  list.setAttribute('data-list-id', listId);
                  list.setAttribute('data-left-margin', '2em');
                  list.setAttribute('data-item-spacing', '0.5em');
                  list.setAttribute('data-marker-space', '0.5em');
                  list.setAttribute('data-list-type', 'decimal');
                  list.classList.add(listId);

                  // Apply default styles
                  list.style.paddingLeft = '2em';
                  list.style.marginBottom = '0.5em';
                }
              }

              // Mark that styles have been restored
              quill.root.setAttribute('data-styles-restored', 'true');
            }
          }
        }, 500);
      }
    }
  }, [quillRef.current, settings.listStyles]); // Run when quillRef.current is ready AND when settings are loaded

  // Manual style restoration function that can be called from anywhere
  const restoreAllListStyles = useCallback(() => {
    if (quillRef.current && Object.keys(settings.listStyles || {}).length > 0) {
      const quill = quillRef.current.getEditor();
      if (quill && quill.root) {
        // Get all lists in the current content
        const lists = quill.root.querySelectorAll('ol, ul');

        // Apply saved styles to each list
        Object.entries(settings.listStyles).forEach(([listId, savedStyles]) => {
          // Try to find the list by ID first
          let listElement = quill.root.querySelector(`[data-list-id="${listId}"]`);

          // If not found by ID, try to find by position and content
          if (!listElement && savedStyles.listPosition !== undefined) {
            // Find list at the saved position
            if (lists[savedStyles.listPosition]) {
              listElement = lists[savedStyles.listPosition];

              // Update the list's ID to match our saved styles
              listElement.setAttribute('data-list-id', listId);
            }
          }

          // If still not found, try to find by matching content structure
          if (!listElement) {
            // Look for a list that has similar structure (same number of items, similar content)
            for (let i = 0; i < lists.length; i++) {
              const list = lists[i];
              const existingId = list.getAttribute('data-list-id');

              // Skip lists that already have IDs (they're already styled)
              if (existingId && existingId !== listId) {
                continue;
              }

              // Check if this list needs styling (no custom styles applied yet)
              if (!list.getAttribute('data-list-styled')) {
                listElement = list;
                break;
              }
            }
          }

          if (listElement) {
            // Re-apply the saved styles
            listElement.setAttribute('data-left-margin', savedStyles.listLeftMargin);
            listElement.setAttribute('data-item-spacing', savedStyles.itemSpacing);
            listElement.setAttribute('data-marker-space', savedStyles.markerSpace);
            listElement.setAttribute('data-list-type', savedStyles.listType);
            listElement.setAttribute('data-list-styled', 'true');
            listElement.setAttribute('data-list-id', listId); // Ensure ID is set

            // Ensure the class is set
            listElement.className = listId;

            // Apply inline styles
            listElement.style.paddingLeft = savedStyles.listLeftMargin;
            listElement.style.marginBottom = savedStyles.itemSpacing;

            // Re-inject CSS for this list
            const styleId = `list-style-${listId}`;
            let styleEl = document.getElementById(styleId);
            if (styleEl) {
              styleEl.remove();
            }

            // Generate CSS for this specific list
            if (listElement.tagName === 'OL') {
              const listType = savedStyles.listType;
              let counterStyle = 'decimal';
              let suffix = '.';
              let contentFormat = null;

              if (listType === 'decimal' || listType === 'decimal-paren') {
                counterStyle = 'decimal';
                suffix = listType === 'decimal-paren' ? ')' : '.';
              } else if (listType === 'decimal-dotted') {
                contentFormat = `counter(custom-counter, decimal) "."`;
              } else if (listType === 'lower-alpha' || listType === 'lower-alpha-paren') {
                counterStyle = 'lower-alpha';
                suffix = listType === 'lower-alpha-paren' ? ')' : '.';
              } else if (listType === 'upper-alpha' || listType === 'upper-alpha-paren') {
                counterStyle = 'upper-alpha';
                suffix = listType === 'upper-alpha-paren' ? ')' : '.';
              }

              const cssContent = `
                html body .ql-editor .${listId},
                html body .ql-editor [data-list-id="${listId}"],
                html body .ql-editor ol.${listId},
                html body .ql-editor ol[data-list-id="${listId}"] {
                  list-style: none !important;
                  counter-reset: custom-counter !important;
                  padding-left: ${savedStyles.listLeftMargin} !important;
                }
                
                html body .ql-editor .${listId} > li,
                html body .ql-editor [data-list-id="${listId}"] > li,
                html body .ql-editor ol.${listId} > li,
                html body .ql-editor ol[data-list-id="${listId}"] > li {
                  position: relative !important;
                  counter-increment: custom-counter !important;
                  margin-bottom: ${savedStyles.itemSpacing} !important;
                }
                
                html body .ql-editor .${listId} > li::before, updateUIForCurrentList
                html body .ql-editor [data-list-id="${listId}"] > li::before,
                html body .ql-editor ol.${listId} > li::before,
                html body .ql-editor ol[data-list-id="${listId}"] > li::before {
                  content: ${contentFormat || `counter(custom-counter, ${counterStyle}) "${suffix}"`} !important;
                  position: absolute !important;
                  left: 0 !important;
                  width: calc(2em + ${savedStyles.markerSpace}) !important;
                  display: inline-block !important;
                  text-align: right !important;
                  color: inherit !important;
                  font-weight: normal !important;
                  font-size: inherit !important;
                  z-index: 9999 !important;
                }
              `;

              styleEl = document.createElement("style");
              styleEl.id = styleId;
              styleEl.innerHTML = cssContent;
              document.head.appendChild(styleEl);

            }
          }
        });
      }
    }
  }, [settings.listStyles]);

  // Expose the restore function globally for debugging
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.restoreListStyles = restoreAllListStyles;
    }
  }, [restoreAllListStyles]);



  // Sync list item colors with their child span colors
  useEffect(() => {
    if (quillRef.current) {
      const quill = quillRef.current.getEditor();
      if (quill) {
        const syncListColors = () => {
          const listItems = quill.root.querySelectorAll('li');
          listItems.forEach(li => {
            const coloredElement = li.querySelector('[style*="color"]');
            if (coloredElement) {
              const color = getComputedStyle(coloredElement).color;
              li.style.setProperty('color', color, 'important');
              li.style.setProperty('--li-color', color, 'important');
              li.setAttribute('data-color', color);
            }
          });
        };

        // Re-apply list styles after every content change
        const reapplyListStyles = () => {
          // Only reapply if we have saved list styles
          if (Object.keys(settings.listStyles || {}).length > 0) {

            // Get all lists in the current content
            const lists = quill.root.querySelectorAll('ol, ul');

            // Apply saved styles to each list using the new levels-based system
            Object.entries(settings.listStyles).forEach(([listId, savedStyles]) => {
              // Try to find the list by custom ID first
              let listElement = quill.root.querySelector(`[data-custom-list-id="${listId}"]`);

              // If not found by custom ID, try data-list-id for backward compatibility
              if (!listElement) {
                listElement = quill.root.querySelector(`[data-list-id="${listId}"]`);
              }

              // If not found by ID, try to find by position
              if (!listElement && savedStyles.listPosition !== undefined) {
                if (lists[savedStyles.listPosition]) {
                  const candidateList = lists[savedStyles.listPosition];
                  // Check if this list already has a different custom ID assigned
                  const existingId = candidateList.getAttribute('data-custom-list-id');
                  if (existingId && existingId !== listId) {
                  } else {
                    listElement = candidateList;
                    // Update the list's ID to match our saved styles
                    listElement.setAttribute('data-custom-list-id', listId);
                  }
                } else {
                }
              }

              // If still not found, try to find an unstyled list or match by position
              if (!listElement) {
                // First, try to find a list without any custom IDs
                for (let i = 0; i < lists.length; i++) {
                  const list = lists[i];
                  const existingCustomId = list.getAttribute('data-custom-list-id');
                  const existingListId = list.getAttribute('data-list-id');

                  // Skip lists that already have different custom IDs
                  if (existingCustomId && existingCustomId !== listId) {
                    continue;
                  }
                  if (existingListId && existingListId !== listId) {
                    continue;
                  }

                  // Use this list if it doesn't have custom styling yet
                  if (!existingCustomId && !existingListId) {
                    listElement = list;
                    listElement.setAttribute('data-custom-list-id', listId);
                    break;
                  }
                }
              }

              // If STILL not found, aggressively try to match by saved position
              if (!listElement && savedStyles.listPosition !== undefined && lists[savedStyles.listPosition]) {
                listElement = lists[savedStyles.listPosition];
                // Force reassign the ID even if it had a different one
                listElement.setAttribute('data-custom-list-id', listId);
              }

              if (listElement) {
                // Ensure the list has the correct custom ID
                listElement.setAttribute('data-custom-list-id', listId);

                // Re-inject the CSS directly since we already have the list element
                const styleId = `list-style-${listId}`;
                let styleEl = document.getElementById(styleId);
                if (styleEl) {
                  styleEl.remove();
                }

                // Generate CSS for this specific list using our levels structure
                if (savedStyles.levels) {
                  const level0 = savedStyles.levels[0] || { ...DEFAULT_LIST_STYLES };
                  const level1 = savedStyles.levels[1] || { ...DEFAULT_LIST_STYLES };
                  const level2 = savedStyles.levels[2] || { ...DEFAULT_LIST_STYLES };

                  // Helper function to generate content based on list type
                  const getContentRule = (level, levelNum) => {
                    const listType = level.listType;
                    if (listType === 'decimal' || listType === 'decimal-paren') {
                      return `counter(l${levelNum}-${listId}, decimal) "${listType === 'decimal-paren' ? ')' : '.'}"`;
                    } else if (listType === 'decimal-dotted') {
                      if (levelNum === 0) {
                        return `counter(l${levelNum}-${listId}, decimal) "."`;
                      } else if (levelNum === 1) {
                        return `counter(l0-${listId}, decimal) "." counter(l${levelNum}-${listId}, decimal)`;
                      } else if (levelNum === 2) {
                        return `counter(l0-${listId}, decimal) "." counter(l1-${listId}, decimal) "." counter(l${levelNum}-${listId}, decimal)`;
                      }
                      return `counter(l${levelNum}-${listId}, decimal) "."`;
                    } else if (listType === 'lower-alpha' || listType === 'lower-alpha-paren') {
                      return `counter(l${levelNum}-${listId}, lower-alpha) "${listType === 'lower-alpha-paren' ? ')' : '.'}"`;
                    } else if (listType === 'upper-alpha' || listType === 'upper-alpha-paren') {
                      return `counter(l${levelNum}-${listId}, upper-alpha) "${listType === 'upper-alpha-paren' ? ')' : '.'}"`;
                    } else if (listType === 'lower-roman') {
                      return `counter(l${levelNum}-${listId}, lower-roman) "."`;
                    } else if (listType === 'upper-roman') {
                      return `counter(l${levelNum}-${listId}, upper-roman) "."`;
                    }
                    return `counter(l${levelNum}-${listId}, decimal) "."`;
                  };

                  let cssContent = `
                    /* Base styles for this specific list */
                    .ql-editor ol[data-custom-list-id="${listId}"] {
                      list-style: none !important;
                      counter-reset: l0-${listId} !important;
                      padding-left: ${level0.listLeftMargin} !important;
                    }
                  `;

                  // Level 0 (no indent class)
                  cssContent += `
                    .ql-editor ol[data-custom-list-id="${listId}"] > li:not([class*="ql-indent-"]) {
                      counter-increment: l0-${listId} !important;
                      counter-reset: l1-${listId} 0 l2-${listId} 0 !important;
                      position: relative !important;
                      padding-left: ${level0.listLeftMargin} !important;
                      margin-bottom: ${level0.itemSpacing} !important;
                    }
                    .ql-editor ol[data-custom-list-id="${listId}"] > li:not([class*="ql-indent-"])::before {
                      content: ${getContentRule(level0, 0)} !important;
                      position: absolute !important;
                      left: 0 !important;
                      width: calc(2em + ${level0.markerSpace}) !important;
                      display: inline-block !important;
                      text-align: right !important;
                      color: currentColor !important;
                      font-size: inherit !important;
                    }
                  `;

                  // Level 1 (ql-indent-1)
                  cssContent += `
                    .ql-editor ol[data-custom-list-id="${listId}"] > li.ql-indent-1 {
                      counter-increment: l1-${listId} !important;
                      counter-reset: l2-${listId} 0 !important;
                      position: relative !important;
                      padding-left: ${level1.listLeftMargin} !important;
                      margin-bottom: ${level1.itemSpacing} !important;
                    }
                    .ql-editor ol[data-custom-list-id="${listId}"] > li.ql-indent-1::before {
                      content: ${getContentRule(level1, 1)} !important;
                      position: absolute !important;
                      left: 0 !important;
                      width: calc(2em + ${level1.markerSpace}) !important;
                      display: inline-block !important;
                      text-align: right !important;
                      color: currentColor !important;
                      font-size: inherit !important;
                    }
                  `;

                  // Level 2 (ql-indent-2)
                  cssContent += `
                    .ql-editor ol[data-custom-list-id="${listId}"] > li.ql-indent-2 {
                      counter-increment: l2-${listId} !important;
                      position: relative !important;
                      padding-left: ${level2.listLeftMargin} !important;
                      margin-bottom: ${level2.itemSpacing} !important;
                    }
                    .ql-editor ol[data-custom-list-id="${listId}"] > li.ql-indent-2::before {
                      content: ${getContentRule(level2, 2)} !important;
                      position: absolute !important;
                      left: 0 !important;
                      width: calc(2em + ${level2.markerSpace}) !important;
                      display: inline-block !important;
                      text-align: right !important;
                      color: currentColor !important;
                      font-size: inherit !important;
                    }
                  `;

                  // Inject the CSS
                  styleEl = document.createElement("style");
                  styleEl.id = styleId;
                  styleEl.innerHTML = cssContent;
                  document.head.appendChild(styleEl);
                }
              }
            });
          }
        };

        // Listen for both text changes and selection changes
        quill.on('text-change', (delta, oldDelta, source) => {

          // For API changes (like formatting), wait longer and batch them
          const isApiChange = source === 'api';
          const debounceTime = isApiChange ? 300 : 100;



          // Debounce the re-application to avoid excessive calls
          // Use longer timeout for API changes to let all formatting settle
          clearTimeout(quill._styleRestoreTimeout);
          quill._styleRestoreTimeout = setTimeout(() => {
            reapplyListStyles();
            syncListColors();
          }, debounceTime);
        });

        // Listen for format changes (when text size, color, etc. changes)
        quill.on('format-change', () => {
          // Immediate restoration for format changes
          clearTimeout(quill._formatStyleTimeout);
          quill._formatStyleTimeout = setTimeout(() => {
            reapplyListStyles();
          }, 10);
        });

        return () => {
          quill.off('text-change');
          quill.off('format-change');
          clearTimeout(quill._styleRestoreTimeout);
          clearTimeout(quill._formatStyleTimeout);
        };
      }
    }
  }, [quillRef.current, settings.listStyles]); // Re-run when listStyles change

  useEffect(() => {
    // Override root font size globally to 14px when TextEditor is mounted
    const originalRootFontSize = document.documentElement.style.fontSize;
    document.documentElement.style.fontSize = '14px';

    // Cleanup: restore original font size when component unmounts
    return () => {
      if (originalRootFontSize) {
        document.documentElement.style.fontSize = originalRootFontSize;
      } else {
        document.documentElement.style.fontSize = '';
      }
    };

    const fontCSS = `
      .ql-font-arial { font-family: Arial, sans-serif; }
      .ql-font-comicsans { font-family: 'Comic Sans MS', cursive, sans-serif; }
      .ql-font-couriernew { font-family: 'Courier New', Courier, monospace; }
      .ql-font-georgia { font-family: Georgia, serif; }
      .ql-font-lucidasans { font-family: 'Lucida Sans', sans-serif; }
      .ql-font-tahoma { font-family: Tahoma, Geneva, sans-serif; }
      .ql-font-timesnewroman { font-family: 'Times New Roman', Times, serif; }
      .ql-font-trebuchet { font-family: 'Trebuchet MS', sans-serif; }
      .ql-font-verdana { font-family: Verdana, Geneva, sans-serif; }
      .ql-font-NSI { font-family: NSI, sans-serif; }
    `;
    const style = document.createElement("style");
    style.innerHTML = fontCSS;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);



  useEffect(() => {
    const styleId = "custom-quill-sizes";
    let styleEl = document.getElementById(styleId);
    if (styleEl) {
      styleEl.remove();
    }

    const css = Object.entries(settings.sizeMap || DEFAULT_SIZE_MAP)
      .map(([label, size]) => `.ql-size-${label} { font-size: ${size} !important; }`)
      .join("\n") + `\n.ql-editor { font-size: ${(settings.sizeMap || DEFAULT_SIZE_MAP).normal} !important; }\n.ql-editor p { font-size: ${(settings.sizeMap || DEFAULT_SIZE_MAP).normal} !important; }`;

    // Add CSS rules to make list markers match the text size and color
    const listMarkerSizeCss = Object.entries(settings.sizeMap || DEFAULT_SIZE_MAP)
      .map(([label, size]) =>
        `.ql-editor li:has(.ql-size-${label}) { font-size: ${size} !important; }\n` +
        `.ql-editor ol > li:has(.ql-size-${label})::before { font-size: ${size} !important; }`
      )
      .join('\n');

    // Add CSS rules to make list markers inherit text color
    const listMarkerColorCss = `
      .ql-editor ol > li::before,
      .ql-editor ul > li::before {
        color: inherit !important;
      }
      
      .ql-editor ol > li.ql-color-*::before,
      .ql-editor ul > li.ql-color-*::before {
        color: inherit !important;
      }
      
      .ql-editor ol > li:has(.ql-color-*)::before,
      .ql-editor ul > li:has(.ql-color-*)::before {
        color: inherit !important;
      }
      
      /* Handle Quill's specific color classes */
      .ql-editor ol > li[class*="ql-color-"]::before,
      .ql-editor ul > li[class*="ql-color-"]::before {
        color: inherit !important;
      }
      
      /* Force color inheritance for all list markers */
      .ql-editor li::before {
        color: inherit !important;
      }
      
      /* Handle inline color styles that Quill might apply */
      .ql-editor li[style*="color"]::before {
        color: inherit !important;
      }
      
      /* Ensure list markers inherit from parent list item's computed color */
      .ql-editor ol > li::before,
      .ql-editor ul > li::before {
        color: currentColor !important;
      }
      
      /* Force list markers to use the --li-color variable when available */
      .ql-editor li[data-color]::before {
        color: var(--li-color) !important;
      }
    `;

    styleEl = document.createElement("style");
    styleEl.id = styleId;
    styleEl.innerHTML = css + '\n' + listMarkerSizeCss + '\n' + listMarkerColorCss;
    document.head.appendChild(styleEl);

    // Force Quill to update if it's mounted
    if (quillRef.current) {
      const quill = quillRef.current.getEditor();
      if (quill) {
        // Force a re-render by updating the editor content
        const currentContent = quill.getContents();
        quill.setContents(currentContent);
      }
    }
  }, [settings.sizeMap]);

  useEffect(() => {

    const styleId = "custom-quill-margins";
    let styleEl = document.getElementById(styleId);
    if (styleEl) {
      styleEl.remove();
    }

    styleEl = document.createElement("style");
    styleEl.id = styleId;
    const cssContent = `
      .ql-editor {
        font-size: ${(settings.sizeMap || DEFAULT_SIZE_MAP).normal} !important;
        padding-top: ${(settings.marginMap || DEFAULT_MARGIN_MAP).top} !important;
        padding-bottom: ${(settings.marginMap || DEFAULT_MARGIN_MAP).bottom} !important;
        padding-left: ${(settings.marginMap || DEFAULT_MARGIN_MAP).left} !important;
        padding-right: ${(settings.marginMap || DEFAULT_MARGIN_MAP).right} !important;
      }

      .ql-editor p {
        font-size: ${(settings.sizeMap || DEFAULT_SIZE_MAP).normal} !important;
        margin-bottom: ${(settings.marginMap || DEFAULT_MARGIN_MAP).paragraphSpacing} !important;
      }

      /* Ultra-high specificity global list styles to override everything */
      html body div.ql-container .ql-editor ol,
      html body .ql-container .ql-editor ol,
      html body .ql-editor ol {
        list-style: none !important;
        counter-reset: custom-counter !important;
        padding-left: ${DEFAULT_LIST_STYLES.listLeftMargin} !important;
      }

      html body div.ql-container .ql-editor ol > li,
      html body .ql-container .ql-editor ol > li,
      html body .ql-editor ol > li {
        counter-increment: custom-counter !important;
        position: relative !important;
        padding-left: ${DEFAULT_LIST_STYLES.listLeftMargin} !important;
        margin-bottom: ${DEFAULT_LIST_STYLES.itemSpacing} !important;
      }

      html body div.ql-container .ql-editor ol > li::before,
      html body .ql-container .ql-editor ol > li::before,
      html body .ql-editor ol > li::before {
        content: counter(custom-counter, decimal) "." !important;
        position: absolute !important;
        left: 0 !important;
        width: calc(2em + ${DEFAULT_LIST_STYLES.markerSpace}) !important;
        display: inline-block !important;
        text-align: right !important;
        color: currentColor !important;
        font-size: inherit !important;
      }

      .ql-editor ul {
        padding-left: ${DEFAULT_LIST_STYLES.listLeftMargin} !important;
      }

      .ql-editor ul > li {
        margin-bottom: ${DEFAULT_LIST_STYLES.itemSpacing} !important;
        padding-left: 0 !important;
      }

      /* Multiple selectors to catch different Quill structures */
      .ql-editor ul,
      .ql-editor .ql-editor ul,
      .ql-editor div ul,
      .ql-editor p ul {
        padding-left: 0 !important;
      }
      
      .ql-editor ul > li,
      .ql-editor .ql-editor ul > li,
      .ql-editor div ul > li,
      .ql-editor p ul > li {
        margin-bottom: ${DEFAULT_LIST_STYLES.itemSpacing} !important;
      }

      /* List marker styling is now handled by global nested list CSS */
    `;




    styleEl.innerHTML = cssContent;
    document.head.appendChild(styleEl);

  }, [settings.marginMap]);





  useEffect(() => {
    setShowEditor(true);
  }, []);

  useEffect(() => {
    const quill = quillRef.current?.getEditor();
    const toolbar = document.getElementById("custom-toolbar");

    if (quill && toolbar) {
      const handleMouseDown = () => {
        selectionRef.current = quill.getSelection();
      };
      const handleMouseUp = () => {
        if (selectionRef.current) {
          quill.setSelection(selectionRef.current);
        }
      };

      toolbar.addEventListener("mousedown", handleMouseDown);
      toolbar.addEventListener("mouseup", handleMouseUp);

      return () => {
        toolbar.removeEventListener("mousedown", handleMouseDown);
        toolbar.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [showEditor]);

  const handleSizeChange = (value) => {
    const quill = quillRef.current?.getEditor();
    if (value === "custom") {
      setShowModal(true);
    } else {
      quill?.format("size", value);
    }
  };

  const handleFontChange = (value) => {
    const quill = quillRef.current?.getEditor();
    quill?.format("font", value);
  };

  const handleSizeInputChange = (label, value) => {
    setSettings(prev => {
      const newSizeMap = { ...(prev.sizeMap || DEFAULT_SIZE_MAP), [label]: `${(value / 14).toFixed(3)}rem` };

      const newSettings = {
        ...prev,
        sizeMap: newSizeMap
      };

      if (quillRef.current) {
        const quill = quillRef.current.getEditor();
        if (quill) {
          const content = quill.root.innerHTML;
          const settingsConfig = {
            sizeMap: newSizeMap,
            marginMap: newSettings.marginMap || DEFAULT_MARGIN_MAP,
            choiceOptions: newSettings.choiceOptions || []
          };
          const escapedJson = JSON.stringify(settingsConfig).replace(/"/g, '&quot;');
          const newValue = `<div data-settings="${escapedJson}">${content}</div>`;

          onChange(newValue);
        }
      }

      return newSettings;
    });
  };

  const handleReset = () => {
    setSettings(prev => ({ ...prev, sizeMap: DEFAULT_SIZE_MAP }));
  };

  const handleMarginInputChange = (label, value) => {
    let cleanedValue;
    if (typeof value === 'string' || typeof value === 'number') {
      const numericValue = parseFloat(value);
      if (!isNaN(numericValue)) {
        const roundedValue = Math.round(numericValue * 10) / 10;
        cleanedValue = `${roundedValue}em`;
      } else {
        cleanedValue = value; // For non-numeric values like listType
      }
    }

    setSettings(prev => {
      const newMarginMap = { ...(prev.marginMap || DEFAULT_MARGIN_MAP), [label]: cleanedValue };

      const newSettings = {
        ...prev,
        marginMap: newMarginMap
      };

      // Create a new value with updated settings
      if (quillRef.current) {
        const quill = quillRef.current.getEditor();
        if (quill) {
          const content = quill.root.innerHTML;
          const settingsConfig = {
            sizeMap: newSettings.sizeMap || DEFAULT_SIZE_MAP,
            marginMap: newMarginMap,
            choiceOptions: newSettings.choiceOptions || []
          };
          const escapedJson = JSON.stringify(settingsConfig).replace(/"/g, '&quot;');
          const newValue = `<div data-settings="${escapedJson}">${content}</div>`;

          onChange(newValue);

          // Let Quill handle the content naturally - no forced re-renders
        }
      }

      return newSettings;
    });
  };

  const handleMarginReset = () => {
    setSettings(prev => ({ ...prev, marginMap: DEFAULT_MARGIN_MAP }));

    if (quillRef.current) {
      const quill = quillRef.current.getEditor();
      if (quill) {
        const content = quill.root.innerHTML;
        const settingsConfig = {
          sizeMap: settings.sizeMap || DEFAULT_SIZE_MAP,
          marginMap: DEFAULT_MARGIN_MAP,
          choiceOptions: settings.choiceOptions || []
        };
        const escapedJson = JSON.stringify(settingsConfig).replace(/"/g, '&quot;');
        const newValue = `<div data-settings="${escapedJson}">${content}</div>`;

        onChange(newValue);

        // Let Quill handle the content naturally - no forced re-renders
      }
    }
  };

  const handleListStyleInputChange = (label, value) => {

    // Parse level-based input format (e.g., "level0-listType", "level1-itemSpacing")
    let level = 0;
    let actualKey = label;

    if (label.startsWith('level')) {
      const match = label.match(/^level(\d+)-(.+)$/);
      if (match) {
        level = parseInt(match[1]);
        actualKey = match[2];
      }
    }

    let cleanedValue;
    if (typeof value === 'string' || typeof value === 'number') {
      const numericValue = parseFloat(value);
      if (!isNaN(numericValue) && actualKey !== 'listType') {
        const roundedValue = Math.round(numericValue * 10) / 10;
        cleanedValue = `${roundedValue}em`;
      } else {
        cleanedValue = value; // For non-numeric values like listType
      }
    }



    // Apply the change to the current list's levels structure
    if (quillRef.current) {
      const quill = quillRef.current.getEditor();
      if (quill) {
        const selection = quill.getSelection() || selectionRef.current;

        if (selection && selection.length > 0) {
          // Find the list element
          const [leaf] = quill.getLeaf(selection.index);
          let domNode = leaf?.domNode;

          while (domNode && !['OL', 'UL'].includes(domNode.tagName)) {
            domNode = domNode.parentElement;
          }

          if (domNode && ['OL', 'UL'].includes(domNode.tagName)) {
            // Get or create list ID
            let listId = domNode.getAttribute('data-custom-list-id');
            if (!listId) {
              listId = `list-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
              domNode.setAttribute('data-custom-list-id', listId);
            }


            // Update the specific level's property
            setSettings(prev => {
              const currentListStyles = prev.listStyles[listId] || {
                levels: {
                  0: { ...DEFAULT_LIST_STYLES }
                },
                listPosition: 0
              };

              const newListStyles = {
                ...currentListStyles,
                levels: {
                  ...currentListStyles.levels,
                  [level]: {
                    ...(currentListStyles.levels[level] || { ...DEFAULT_LIST_STYLES }),
                    [actualKey]: cleanedValue
                  }
                }
              };


              // Apply the updated styles immediately
              setTimeout(() => {
                applyListStyleToSelection(quill, selection, newListStyles);
              }, 10);

              return {
                ...prev,
                listStyles: {
                  ...prev.listStyles,
                  [listId]: newListStyles
                }
              };
            });

            // Update currentListId so UI reflects the current list
            setCurrentListId(listId);
          } else {
          }
        }

        // DON'T call onChange here - it triggers Quill re-render and strips our data attributes!

        // Update the input value directly in DOM to show the new value (without 'em' suffix)
        const inputElement = document.querySelector(`input[placeholder="e.g. 2"]`);
        if (inputElement) {
          const numericValue = cleanedValue.replace('em', '');
          if (inputElement.value !== numericValue) {
            inputElement.value = numericValue;
          }
        }
      }
    }

    // No need to update listStyleMap anymore - UI now gets values from getCurrentListStyles()
  };

  const handleListStyleReset = () => {
    // Reset is now handled by clearing currentListId
    setCurrentListId(null);

    if (quillRef.current) {
      const quill = quillRef.current.getEditor();
      if (quill) {
        // Reset functionality is now handled by the UI showing defaults
        // when no list is selected
      }
    }
  };

  const applyListStyleToSelection = (quill, selection, listStyles) => {
    const { index, length } = selection;

    // Find the current list element in the DOM to get a unique identifier
    const [leaf] = quill.getLeaf(index);
    let domNode = leaf?.domNode;

    // Find the closest list element
    while (domNode && !['OL', 'UL'].includes(domNode.tagName)) {
      domNode = domNode.parentElement;
    }

    if (!domNode) {
      return;
    }

    // Generate a unique ID for this specific list instance
    let listId = domNode.getAttribute('data-custom-list-id');
    if (!listId) {
      listId = `list-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      domNode.setAttribute('data-custom-list-id', listId);
    } else {
    }

    // Convert old flat structure to new levels structure if needed
    const levelsStructure = listStyles.levels ? listStyles : {
      levels: {
        0: {
          listType: listStyles.listType || "decimal",
          listLeftMargin: listStyles.listLeftMargin || "2em",
          itemSpacing: listStyles.itemSpacing || "0.5em",
          markerSpace: listStyles.markerSpace || "0.5em"
        },
        1: {
          listType: "lower-alpha",
          listLeftMargin: "2.5em",
          itemSpacing: "0.4em",
          markerSpace: "0.6em"
        },
        2: {
          listType: "lower-roman",
          listLeftMargin: "3em",
          itemSpacing: "0.3em",
          markerSpace: "0.5em"
        }
      },
      listPosition: listStyles.listPosition || 0
    };

    // Store the styles for this specific list
    setSettings(prev => ({
      ...prev,
      listStyles: {
        ...prev.listStyles,
        [listId]: levelsStructure
      }
    }));

    // Generate CSS that targets this specific list using ql-indent classes
    const styleId = `list-style-${listId}`;
    let styleEl = document.getElementById(styleId);
    if (styleEl) {
      styleEl.remove();
    }


    // Helper function to generate content based on list type
    const getContentRule = (level, listId, levelNum) => {
      const listType = level.listType;
      if (listType === 'decimal' || listType === 'decimal-paren') {
        return `counter(l${levelNum}-${listId}, decimal) "${listType === 'decimal-paren' ? ')' : '.'}"`;
      } else if (listType === 'decimal-dotted') {
        // For decimal-dotted format: X.1, X.2, X.3 where X is the parent list item number
        if (levelNum === 0) {
          return `counter(l${levelNum}-${listId}, decimal) "."`;
        } else {
          // Build counter chain dynamically for unlimited nesting levels
          const counterChain = [];
          for (let i = 0; i <= levelNum; i++) {
            counterChain.push(`counter(l${i}-${listId}, decimal)`);
          }
          return counterChain.join(' "." ');
        }
      } else if (listType === 'lower-alpha' || listType === 'lower-alpha-paren') {
        return `counter(l${levelNum}-${listId}, lower-alpha) "${listType === 'lower-alpha-paren' ? ')' : '.'}"`;
      } else if (listType === 'upper-alpha' || listType === 'upper-alpha-paren') {
        return `counter(l${levelNum}-${listId}, upper-alpha) "${listType === 'upper-alpha-paren' ? ')' : '.'}"`;
      } else if (listType === 'lower-roman') {
        return `counter(l${levelNum}-${listId}, lower-roman) "."`;
      } else if (listType === 'upper-roman') {
        return `counter(l${levelNum}-${listId}, upper-roman) "."`;
      }
      return `counter(l${levelNum}-${listId}, decimal) "."`;
    };

    // Get all counter names for counter-reset
    const allLevels = Object.keys(levelsStructure.levels).map(Number).sort((a, b) => a - b);
    const counterResetList = allLevels.map(level => `l${level}-${listId}`).join(' ');

    // Base styles with dynamic counter reset for all levels
    const level0 = levelsStructure.levels[0] || { ...DEFAULT_LIST_STYLES };
    let cssContent = `
      /* Base styles for this specific list - higher specificity than global */
      .ql-editor ol[data-custom-list-id="${listId}"] {
        list-style: none !important;
        counter-reset: ${counterResetList} !important;
        padding-left: ${level0.listLeftMargin} !important;
      }
    `;

    // Generate CSS for each level dynamically
    allLevels.forEach(levelNum => {
      const levelData = levelsStructure.levels[levelNum];
      if (!levelData) return;

      // Generate counter-reset for child levels
      const childLevels = allLevels.filter(l => l > levelNum);
      const childCounterReset = childLevels.length > 0
        ? `counter-reset: ${childLevels.map(l => `l${l}-${listId} 0`).join(' ')} !important;`
        : '';

      // Level selector
      const levelSelector = levelNum === 0
        ? ':not([class*="ql-indent-"])'
        : `.ql-indent-${levelNum}`;

      cssContent += `
        .ql-editor ol[data-custom-list-id="${listId}"] > li${levelSelector} {
          counter-increment: l${levelNum}-${listId} !important;
          ${childCounterReset}
          position: relative !important;
          padding-left: ${levelData.listLeftMargin} !important;
          margin-bottom: ${levelData.itemSpacing} !important;
        }
        .ql-editor ol[data-custom-list-id="${listId}"] > li${levelSelector}::before {
          content: ${getContentRule(levelData, listId, levelNum)} !important;
          position: absolute !important;
          left: 0 !important;
          width: calc(2em + ${levelData.markerSpace}) !important;
          display: inline-block !important;
          text-align: right !important;
          color: currentColor !important;
          font-size: inherit !important;
        }
      `;
    });

    // Inject the CSS
    styleEl = document.createElement("style");
    styleEl.id = styleId;
    styleEl.innerHTML = cssContent;
    document.head.appendChild(styleEl);
  };

  const getWrappedHtml = () => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return "";
    const content = quill.root.innerHTML;

    const settingsConfig = {
      sizeMap: settings.sizeMap || DEFAULT_SIZE_MAP,
      marginMap: settings.marginMap || DEFAULT_MARGIN_MAP,
      // Include individual list styles
      listStyles: settings.listStyles || {},
      choiceOptions: settings.choiceOptions || []
    };

    const escapedJson = JSON.stringify(settingsConfig).replace(/"/g, '&quot;');
    const wrappedHtml = `<div data-settings="${escapedJson}">${content}</div>`;


    return wrappedHtml;
  };

  useEffect(() => {
    if (getWrappedHtmlRef) {
      getWrappedHtmlRef.current = getWrappedHtml;
    }
  }, [getWrappedHtmlRef, settings.sizeMap, settings.marginMap, settings.listStyles]);

  const updateChoiceOptions = (choice) => {
    if (!settings.choiceOptions) {
      setSettings(prev => ({ ...prev, choiceOptions: [choice] }));
      return;
    }

    const exists = settings.choiceOptions.find(o => o.data.button_text === choice.data.button_text);
    if (choice.hasOwnProperty('enabled')) delete choice.enabled;
    if (!exists) {
      setSettings(prev => ({ ...prev, choiceOptions: [...prev.choiceOptions, choice] }));
    }
  }

  const getCleanMarginValue = (key) => {
    const value = (settings.marginMap || DEFAULT_MARGIN_MAP)[key];
    if (typeof value === 'string' && value.includes('em')) {
      const numericValue = parseFloat(value);
      if (!isNaN(numericValue)) {
        return Math.round(numericValue * 10) / 10;
      }
    }
    return value;
  };

  const getCleanListStyleValue = (key) => {
    const currentStyles = getCurrentListStyles();
    const value = currentStyles[key];

    if (typeof value === 'string' && value.includes('em')) {
      const numericValue = parseFloat(value);
      if (!isNaN(numericValue)) {
        return Math.round(numericValue * 10) / 10;
      }
    }
    return value;
  };

  const fontOptions = isNSI(accountId)
    ? [...FONT_OPTIONS, { label: "NS&I Everyday", value: 'NSI', style: { fontFamily: "NSI, sans-serif" } }]
    : FONT_OPTIONS
  return (
    <div className={`text-editor ${disabled ? "disabled" : ""}`}>
      <div id="custom-toolbar" className='editor-toolbar'>
        <Select defaultValue="arial" onChange={handleFontChange} style={{ width: 280 }}>
          {fontOptions.map(({ label, value, style }) => (
            <Option key={value} value={value}>
              <span style={style}>{label}</span>
            </Option>
          ))}
        </Select>

        <Select defaultValue="normal" onChange={handleSizeChange} style={{ width: 280 }}>
          <Option value="custom">
            <Tooltip title="You can define custom pixel values for each of the preset options">
              <span>Set custom values</span>
            </Tooltip>
          </Option>
          <Option value="small">Small</Option>
          <Option value="normal">Normal</Option>
          <Option value="large">Large</Option>
          <Option value="huge">Huge</Option>
          <Option value="custom1">Custom 1</Option>
          <Option value="custom2">Custom 2</Option>
        </Select>

        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setShowMarginModal(true);
          }}
          style={{ minWidth: '120px' }}
        >
          Custom Settings
        </button>

        <span className="toolbar-group">
          <button className="ql-bold" />
          <button className="ql-italic" />
          <button className="ql-underline" />
          <button className="ql-strike" />
          <select className="ql-color" />
          <select className="ql-background" />
          <div style={{ display: 'inline-block', marginLeft: 8 }}>
            <input
              type="color"
              defaultValue="#000000"
              onChange={(e) => {
                const quill = quillRef.current?.getEditor();
                quill?.format("color", e.target.value);
              }}
              style={{
                width: 18,
                height: 18,
                border: '2px solid rgba(114, 46, 209, 0.8)',
                borderRadius: 3,
                cursor: 'pointer',
                padding: 0
              }}
              title="Pick a color"
            />
          </div>
        </span>

        <span className="toolbar-group">
          <button className="ql-list" value="ordered" />
          <button className="ql-list" value="bullet" />
          <button className="ql-indent" value="+1" />
          <button className="ql-indent" value="-1" />
          <select className="ql-align" />
        </span>

        <span className="toolbar-group">
          <button className="ql-link" />
          <button className="ql-image" />
          {features.includes('custom_choices') && isApp && (
            <ChoiceOptions
              ref={coRef}
              accountId={22}
              features={features}
              updateActions={updateChoiceOptions}
              messageSubcategory='legal_doc'
              category='legal_doc'
              isApp={isApp}
              choiceOptions={settings.choiceOptions}
            />
          )}
          <Phone
            ref={phoneRef}
          />
        </span>
      </div>

      {showEditor && (
        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={typeof value === "string" ? value : ""}
          onChange={(content, delta, source, editor) => {
            // Use clean editor content for the form value
            const cleanHtml = editor.getHTML();

            // Log HTML structure when document is loaded
            if (source === 'api' && quillRef.current) {
              const quill = quillRef.current.getEditor();
              if (quill && quill.root) {
              }
            }

            // Log what we're saving
            if (source === 'user') {
            }

            onChange(cleanHtml);
          }}
          modules={modulesMemo}
          formats={["font", "size", "align", "bold", "italic", "underline", "strike", "list", "bullet", "indent", "outdent", "color", "background", "link", "image", "linkChoiceOptions", "phone"]}
          placeholder="Type or paste your text here..."
          style={{ height: 488 }}
          readOnly={disabled}
        />
      )}

      <Modal
        title="Set custom values"
        open={showModal}
        onCancel={() => setShowModal(false)}
        footer={[
          <Button key="reset" onClick={handleReset}>Reset all</Button>,
          <Button key="cancel" onClick={() => setShowModal(false)}>Cancel</Button>,
          <Button
            key="save"
            type="primary"
            onClick={() => {
              setShowModal(false);
            }}
          >
            Save
          </Button>
        ]}
      >
        {Object.entries(settings.sizeMap || DEFAULT_SIZE_MAP).map(([label, remValue]) => {
          const pxValue = Math.round(parseFloat(remValue) * 14);
          return (
            <div key={label} style={{ display: "flex", alignItems: "center", marginBottom: 16 }}>
              <label style={{ width: 80 }}>{label.charAt(0).toUpperCase() + label.slice(1)}:</label>
              <Input
                type="number"
                value={pxValue}
                onChange={(e) => handleSizeInputChange(label, e.target.value)}
                addonAfter="px"
                style={{ width: 100 }}
              />
            </div>
          );
        })}
      </Modal>

      <Modal
        title="Custom settings"
        open={showMarginModal}
        onCancel={() => {
          setShowMarginModal(false);
        }}
        footer={[
          <Button key="reset" onClick={() => {
            handleMarginReset();
          }}>
            Reset all
          </Button>,
          <Button key="cancel" onClick={() => {
            setShowMarginModal(false);
          }}>
            Cancel
          </Button>,
          <Button key="save" type="primary" onClick={() => {
            setShowMarginModal(false);
          }}>
            Save
          </Button>,
        ]}
      >
        {[
          ["Top Margin", "top"],
          ["Bottom Margin", "bottom"],
          ["Left Margin", "left"],
          ["Right Margin", "right"],
          ["Paragraph Spacing", "paragraphSpacing"],
        ].map(([label, key]) => (
          <div key={key} style={{ display: "flex", alignItems: "center", marginBottom: 16 }}>
            <label style={{ width: 180 }}>{label}:</label>
            <Input
              type="number"
              step="0.1"
              value={getCleanMarginValue(key)}
              onChange={(e) => handleMarginInputChange(key, e.target.value)}
              placeholder="e.g. 2"
              style={{ width: 120 }}
              addonAfter="em"
            />
          </div>
        ))}

        <div style={{ marginTop: 24, marginBottom: 16, borderTop: "1px solid #d9d9d9", paddingTop: 16 }}>
          <h4 style={{ margin: 0, color: "#666" }}>List settings</h4>
          <p style={{ margin: "8px 0 16px 0", color: "#999", fontSize: "12px" }}>
            Highlight a list item in the editor to customize the list's appearance.
          </p>
        </div>

        {(() => {
          const currentLevel = getCurrentIndentLevel();

          // Check if the current list actually has nested levels
          const hasNestedLevels = currentListId && settings.listStyles[currentListId] &&
            settings.listStyles[currentListId].levels &&
            Object.keys(settings.listStyles[currentListId].levels).length > 1;



          const levelNames = ["Top level", "First nested", "Second nested"];

          return (
            <div style={{ marginBottom: 24, padding: 16, border: "1px solid #d9d9d9", borderRadius: "6px" }}>
              <h5 style={{ margin: "0 0 12px 0", color: "#333" }}>
                {hasNestedLevels ?
                  `Level ${currentLevel} (${levelNames[currentLevel] || `Level ${currentLevel}`})` :
                  'List Settings'
                }
              </h5>

              {[
                ["Left Margin", "listLeftMargin"],
                ["Items Spacing", "itemSpacing"],
                ["Number Indentation", "markerSpace"],
              ].map(([label, key]) => (
                <div key={`${currentLevel}-${key}`} style={{ display: "flex", alignItems: "center", marginBottom: 12 }}>
                  <label style={{ width: 140, fontSize: "13px" }}>{label}:</label>
                  <Input
                    type="number"
                    step="0.1"
                    value={parseFloat(getCurrentListStyles()[key]) || 0}
                    onMouseDown={() => {
                      // Store current Quill selection BEFORE input steals focus
                      if (quillRef.current) {
                        const quill = quillRef.current.getEditor();
                        if (quill) {
                          const selection = quill.getSelection();
                          if (selection && selection.length > 0) {
                            selectionRef.current = selection;
                          }
                        }
                      }
                    }}
                    onChange={(e) => handleListStyleInputChange(`level${currentLevel}-${key}`, e.target.value)}
                    placeholder="e.g. 2"
                    style={{ width: 100 }}
                    addonAfter="em"
                  />
                </div>
              ))}

              <div style={{ display: "flex", alignItems: "center", marginBottom: 12 }}>
                <label style={{ width: 140, fontSize: "13px" }}>List Type:</label>
                <select
                  value={getCurrentListStyles().listType || "decimal"}
                  onChange={(e) => handleListStyleInputChange(`level${currentLevel}-listType`, e.target.value)}
                  style={{ width: 180, padding: "4px 8px", fontSize: "13px" }}
                >
                  <option value="decimal">1. 2. 3.</option>
                  <option value="decimal-paren">1) 2) 3)</option>
                  {currentLevel > 0 && <option value="decimal-dotted">X.1 X.2 X.3</option>}
                  <option value="lower-alpha">a. b. c.</option>
                  <option value="lower-alpha-paren">a) b) c)</option>
                  <option value="upper-alpha">A. B. C.</option>
                  <option value="upper-alpha-paren">A) B) C)</option>
                  <option value="lower-roman">i. ii. iii.</option>
                  <option value="upper-roman">I. II. III.</option>
                </select>
              </div>
            </div>
          );
        })()}




        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
          <Button onClick={handleListStyleReset} style={{ marginRight: 8 }}>
            Reset list styles
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default TextEditor
